/**
 * Photo upload — one upload button per photograph, each showing a thumbnail of
 * the chosen image.
 *
 * Each photograph is uploaded to the Cloudflare Worker, which stores it in R2
 * and returns its URL. The slot's name (top, middle, bottom) rides along as the
 * Worker's `frame` field, so the stored file is labelled by position. The URLs
 * are written to the add-to-cart form as `Artwork 1`, `Artwork 2`, ... — the
 * property names the orders webhook packs per order.
 */

/**
 * The page has several `form[action*="/cart/add"]` elements (Shop Pay
 * installments, etc.); only the one with the add button submits, so line-item
 * properties are written there.
 */
function cartForm() {
  const forms = [...document.querySelectorAll('form[action*="/cart/add"]')];
  return forms.find((f) => f.querySelector('button[name="add"]')) || forms[0] || null;
}

if (!customElements.get('photo-upload')) {
  customElements.define(
    'photo-upload',
    class PhotoUpload extends HTMLElement {
      connectedCallback() {
        this.endpoint = (this.dataset.endpoint || '').replace(/\/$/, '');
        this.kind = this.dataset.kind || 'photo';
        this.product = this.dataset.product || '';
        this.requirePhoto = this.dataset.requirePhoto === 'true';
        this.notice = this.querySelector('[data-notice]');
        this.slots = [...this.querySelectorAll('[data-slot]')].map((el) => ({
          el,
          key: el.dataset.key || '',
          tile: el.querySelector('[data-tile]'),
          input: el.querySelector('[data-file]'),
          thumb: el.querySelector('[data-thumb]'),
          clear: el.querySelector('[data-clear]'),
          url: null,
          status: 'empty',
        }));

        this.slots.forEach((slot, i) => this.bindSlot(slot, i));
        this.gateSubmit();
      }

      bindSlot(slot, index) {
        slot.tile.addEventListener('click', () => slot.input.click());
        slot.input.addEventListener('change', () => {
          const file = slot.input.files && slot.input.files[0];
          if (file) this.setPhoto(slot, index, file);
        });
        slot.clear.addEventListener('click', (event) => {
          event.stopPropagation();
          this.clearSlot(slot);
        });
      }

      setPhoto(slot, index, file) {
        if (slot.preview) URL.revokeObjectURL(slot.preview);
        slot.preview = URL.createObjectURL(file);
        slot.thumb.src = slot.preview;
        slot.thumb.hidden = false;
        slot.tile.classList.add('has-photo');
        slot.clear.hidden = false;
        this.upload(slot, index, file);
      }

      clearSlot(slot) {
        if (slot.preview) URL.revokeObjectURL(slot.preview);
        slot.preview = null;
        slot.url = null;
        slot.status = 'empty';
        slot.thumb.removeAttribute('src');
        slot.thumb.hidden = true;
        slot.tile.classList.remove('has-photo', 'is-error', 'is-missing');
        slot.clear.hidden = true;
        slot.input.value = '';
        this.syncProperties();
        this.hideNotice();
      }

      async upload(slot, index, file) {
        slot.status = 'uploading';
        slot.tile.classList.add('is-uploading');
        const body = new FormData();
        body.append('file', file);
        body.append('kind', this.kind);
        body.append('product', this.product);
        body.append('frame', slot.key);
        try {
          const response = await fetch(`${this.endpoint}/upload`, { method: 'POST', body });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.error || `upload failed (${response.status})`);
          slot.url = data.url;
          slot.status = 'ready';
        } catch (error) {
          slot.status = 'error';
          slot.error = error.message || 'upload failed';
          console.error('photo-upload: upload failed', error);
        }
        slot.tile.classList.remove('is-uploading');
        slot.tile.classList.toggle('is-error', slot.status === 'error');
        this.syncProperties();
        this.reportState();
      }

      /** The photograph URLs become the cart line's line-item properties. */
      syncProperties() {
        const form = cartForm();
        if (!form) return;
        const write = (name, value) => {
          let input = form.querySelector(`input[name="${name}"]`);
          if (!value) {
            input?.remove();
            return;
          }
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            form.appendChild(input);
          }
          input.value = value;
        };
        this.slots.forEach((slot, i) => {
          write(`properties[Artwork ${i + 1}]`, slot.url || '');
        });
      }

      /**
       * Nothing goes to the cart while a photograph is uploading or has failed.
       * A failed slot carries no URL, so its property is dropped — letting the
       * submit through would add a line item with no artwork on it at all. When
       * the block requires a photograph, a slot that was never filled blocks too.
       */
      gateSubmit() {
        const form = cartForm();
        if (!form) return;
        form.addEventListener(
          'submit',
          (event) => {
            const uploading = this.slots.some((s) => s.status === 'uploading');
            const failed = this.slots.some((s) => s.status === 'error');
            const missing = this.requirePhoto ? this.slots.filter((s) => s.status === 'empty') : [];
            if (!uploading && !failed && !missing.length) {
              this.markMissing([]);
              this.clearFormError();
              return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            const message = uploading
              ? 'Hold on — your photograph is still uploading. Give it a moment and try again.'
              : failed
                ? 'A photograph did not upload. Remove and re-add it, then try again.'
                : 'Add your photograph before you order — we print the photo you upload.';
            this.markMissing(missing);
            this.showNotice(message);
            this.showFormError(message);
          },
          true
        );
      }

      /** Outline the slots that still need a photograph. */
      markMissing(slots) {
        const flagged = new Set(slots.map((s) => s.el));
        this.slots.forEach((slot) => slot.tile.classList.toggle('is-missing', flagged.has(slot.el)));
      }

      /** Reuse the theme's own error region above the Add to cart button. */
      showFormError(message) {
        const productForm = cartForm()?.closest('product-form');
        if (!productForm || productForm.hideErrors) return;

        if (typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage(message);
          return;
        }

        const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
        const slot = wrapper?.querySelector('.product-form__error-message');
        if (!wrapper || !slot) return;
        slot.textContent = message;
        wrapper.hidden = false;
      }

      clearFormError() {
        const productForm = cartForm()?.closest('product-form');
        if (!productForm || productForm.hideErrors) return;

        if (typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage(false);
          return;
        }

        const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
        if (wrapper) wrapper.hidden = true;
      }

      reportState() {
        if (this.slots.some((s) => s.status === 'error')) {
          this.showNotice('A photograph did not upload. Please try again.');
        } else {
          this.hideNotice();
        }
        if (this.slots.every((s) => s.status === 'ready')) this.markMissing([]);
      }

      showNotice(text) {
        if (!this.notice) return;
        this.notice.textContent = text;
        this.notice.hidden = false;
      }

      hideNotice() {
        if (this.notice) this.notice.hidden = true;
        this.clearFormError();
      }
    }
  );
}
